import {
  Collection,
  CollectionCheckUpdate,
  CollectionCreateParams,
  CollectionDisseminateManifestParams,
  CollectionDisseminateMode,
  CollectionDisseminateTemplate,
  CollectionGet,
  CollectionItem,
  CollectionPublishParams,
  CollectionRemoveParams,
  CollectionType,
  CollectionUpdateParams,
  CollectionWithdrawManifestParams,
  RefferedBy,
  ServiceRaw
} from '@aipb/gulliver/personal-content/model';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, Subject } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { CollectionApiService } from '../../api';
import { FavoriteCollectionRepository } from './favorite-collection.repository';

@Injectable({
  providedIn: 'root'
})
export class FavoriteCollectionService {
  private _created: Subject<CollectionGet> = new Subject<CollectionGet>();
  public created$: Observable<CollectionGet> = this._created.asObservable();

  constructor(
    private api: CollectionApiService,
    private transloco: TranslocoService,
    private repository: FavoriteCollectionRepository
  ) {}

  public setActive(id: string): void {
    this.repository.setActiveCollection(id);
  }

  public create(collection: CollectionCreateParams) {
    return this.api.create(collection).pipe(tap((data) => this._created.next(data)));
  }

  public publish(
    id: string,
    template: CollectionDisseminateTemplate,
    params: CollectionPublishParams,
    checkNum: number,
    mode?: CollectionDisseminateMode
  ) {
    const p = {
      id: id,
      template: template,
      ...params
    };

    if (mode) {
      p['mode'] = mode;
    }

    if (checkNum) {
      p['updateCheckNum'] = checkNum;
    }

    return this.api.disseminate(p);
  }

  public get(id: string, referredBy: RefferedBy = 'off', serviceRaw: ServiceRaw = 'off') {
    this.repository.setCollectionLoading(id, true);
    this.repository.setLoading(true);
    return this.api
      .get({
        id,
        referredBy,
        serviceRaw
      })
      .pipe(
        tap((data) => {
          this.repository.updateCollection(id, data.collection);
        }),
        finalize(() => {
          this.repository.setCollectionLoading(id, false);
          this.repository.setLoading(false);
        })
      );
  }

  public getByUniqueId(id: string) {
    return this.api
      .get({
        uniqueId: id
      })
      .pipe(
        tap((data) => {
          this.repository.addCollection(data.collection);
        })
      );
  }

  public getAncestors(id: number) {
    return this.api.getAncestors({
      id: id
    });
  }

  public remove(id: string, force = false) {
    const params: CollectionRemoveParams = {
      id
    };

    if (force) {
      params.forceRemove = true;
    }

    return this.api.remove(params).pipe(
      tap(() => {
        this.repository.removeCollection(id);
      })
    );
  }

  public update(collection: CollectionUpdateParams): Observable<CollectionGet> {
    return this.api.update(collection).pipe(
      tap((data) => {
        this.repository.updateCollection(collection.id, data.collection);
      })
    );
  }

  public removeAll(): void {
    this.repository.removeAllCollections();
  }

  public updateProperty(id: string, updateCheckNum: number, propertyName: string, propertyValue: any) {
    this.repository.setCollectionLoading(id, true);
    return this.api
      .update({
        id: id,
        [propertyName]: propertyValue,
        updateCheckNum: updateCheckNum
      })
      .pipe(
        tap((data) => {
          this.repository.updateCollection(id, data.collection);
        }),
        finalize(() => {
          this.repository.setCollectionLoading(id, false);
        })
      );
  }

  public withdraw(id: string, template?: CollectionDisseminateTemplate) {
    return this.api.withdraw({
      id: id,
      template: template
    });
  }

  public checkUpdate(id: string): Observable<CollectionCheckUpdate> {
    return this.api.checkUpdate({
      id: id
    });
  }

  public disseminateManifest(params: CollectionDisseminateManifestParams): Observable<any> {
    return this.api.disseminateManifest(params);
  }

  public withdrawManifest(params: CollectionWithdrawManifestParams): Observable<any> {
    return this.api.withdrawManifest(params);
  }

  public getCollectionLabel(collection: Collection | CollectionItem): string {
    if (!collection) return '';

    if (collection.type === 'favorites') {
      return this.transloco.translate('personal_content.collection_types.favorites');
    }

    return collection.label;
  }

  public isFavorite(type: CollectionType): boolean {
    return type === 'favorites';
  }

  public isGeneral(type: CollectionType): boolean {
    return type === 'general';
  }

  public isDidactic(type: CollectionType): boolean {
    return type === 'didactic';
  }

  public isTrashbin(type: CollectionType): boolean {
    return type === 'trashbin';
  }
}
